/**
 * Course Materials Page Controller
 * Main coordinator for the course materials page functionality
 */
function cmrpStudentIdLabel() {
  if (typeof window !== 'undefined' && typeof window.getCmrpStudentIdLabel === 'function') {
    return window.getCmrpStudentIdLabel();
  }
  const s = typeof window !== 'undefined' ? window.shopifySettings?.cmrpFlowSwitcherStudentPlaceholder : undefined;
  const t = s != null ? String(s).trim() : '';
  return t || 'Student ID';
}

class CourseMaterialsPage {
  /** Query param carrying a ConnectOnce discovery error code into course builder */
  static SID_ERROR_PARAM = 'sidError';
  /** sidError value for the CMRP script-load failure (not a ConnectOnce code) */
  static SYSTEM_LOAD_ERROR_CODE = 'load';
  static SYSTEM_LOAD_FAILURE_MESSAGE =
    "We couldn't find these details right now. Please refresh the page, then select your courses to continue.";

  constructor(courseData) {
    this.courseData = courseData;
    this.selectedFilters = [];
    this.components = {};
    this.previousCampusValue = '';
    this.warningId =
      typeof window.cmrpId === 'function'
        ? window.cmrpId('student-id-error-warning', 'sid')
        : 'student-id-error-warning';
    this.mobileWarningId = 'mobile-student-id-error-warning';

    this.URL_PARAMS = {
      SHOP_BY: 'shopBy',
      TERM_ID: 'termId',
      DIVISION_DISPLAY_NAME: 'divisionDisplayName',
      DEPARTMENT_DISPLAY_NAME: 'departmentDisplayName',
      COURSE_DISPLAY_NAME: 'courseDisplayName',
      COURSE_REF_ID: 'courseRefId',
      SECTION_DISPLAY_NAME: 'sectionDisplayName',
      SID_ERROR: CourseMaterialsPage.SID_ERROR_PARAM,
    };

    this.init();
  }

  /**
   * Initialize the page and all its components
   */
  init() {
    // Add page class for styling (html too — avoids html:has(body.course-materials-page))
    document.documentElement.classList.add('course-materials-page');
    document.body.classList.add('course-materials-page');
    if (document.querySelector('.discover-view-flow')) {
      document.documentElement.classList.add('discover-view-flow');
      document.body.classList.add('discover-view-flow');
    }

    // Restore last session URL BEFORE sidebar reads location.search
    if (typeof window.CmrpSessionSnapshot?.tryRestoreResultsUrl === 'function') {
      window.CmrpSessionSnapshot.tryRestoreResultsUrl();
    }

    // Initialize components
    this.initializeComponents();

    // Setup event listeners
    this.setupEventListeners();

    // Initialize UI state
    this.initializeUI();

    // Auto-open sidebar on desktop
    this.handleDesktopSidebar();

    // Student ID discovery failures redirect here with a pending banner message
    this.consumePendingCourseBuilderError();

    // Keep Course Materials List sticky offset flush with the visible site header
    this.bindCmrpListHeaderStickyOffset();
    this.bindSkipToCourseMaterials();
    this.bindStickyCtaFocusScroll();
  }

  /**
   * Sync `--cmrp-list-header-top` to the visible bottom of the site header.
   * Theme `--header-height` is measured on #header-component and can stay tall
   * while sticky mode hides .header__row--search — using it as `top` leaves a gap.
   *
   * Do not write the var on body (invalidates the whole page). Do not run a
   * getBoundingClientRect + setProperty loop on every scroll tick while the
   * header is pinned — height only changes on sticky-state / resize.
   */
  bindCmrpListHeaderStickyOffset() {
    let rafId = 0;
    let scrollListening = false;
    let lastValue = '';
    let requirementsOpen = false;
    let savedScrollY = 0;
    let settleFrames = 0;

    const headerEl = () =>
      document.querySelector('.header-section') ||
      document.querySelector('#header-component');

    const apply = px => {
      const value = `${px}px`;
      if (value === lastValue) return;
      lastValue = value;
      const listHeader = document.getElementById('cmrp-list-header');
      const target = listHeader || document.documentElement;
      target.style.setProperty('--cmrp-list-header-top', value);
      if (listHeader) {
        document.documentElement.style.removeProperty('--cmrp-list-header-top');
      }
    };

    const stopScroll = () => {
      if (!scrollListening) return;
      scrollListening = false;
      window.removeEventListener('scroll', schedule);
    };

    const startScroll = () => {
      if (scrollListening) return;
      scrollListening = true;
      window.addEventListener('scroll', schedule, { passive: true });
    };

    const pageScrollY = () => window.scrollY || document.documentElement.scrollTop || 0;

    const requirementsIsOpen = () =>
      document.getElementById('mobile-drawer-requirements')?.classList.contains('mobile-drawer-open') ===
        true || document.querySelector('dialog.requirements-dialog-element')?.open === true;

    // Closing Requirements clears overflow and the page can report scroll 0 for
    // a few frames. Hold the sticky measurement and put the scroll position back
    // so the header does not jump.
    const holdMenuPush = () => {
      const open = requirementsIsOpen();
      if (open) {
        if (!requirementsOpen) savedScrollY = pageScrollY();
        requirementsOpen = true;
        settleFrames = 0;
        return true;
      }

      if (requirementsOpen) {
        requirementsOpen = false;
        settleFrames = 24;
      }

      if (settleFrames <= 0) return false;

      if (Math.abs(pageScrollY() - savedScrollY) > 2) {
        window.scrollTo(0, savedScrollY);
      }
      settleFrames -= 1;

      const scrollSettled = Math.abs(pageScrollY() - savedScrollY) <= 2;

      if (settleFrames > 0 && !scrollSettled) {
        return true;
      }

      settleFrames = 0;
      return false;
    };

    const sync = () => {
      rafId = 0;
      if (holdMenuPush()) {
        schedule();
        return;
      }

      const header = headerEl();
      if (!header) {
        apply(0);
        stopScroll();
        return;
      }
      const rect = header.getBoundingClientRect();
      apply(Math.max(0, Math.round(rect.bottom)));
      // Pinned at the viewport top: further scroll does not move header.bottom.
      // Re-listen while unpinned (announcement bar still occupying space).
      if (rect.top <= 1) stopScroll();
      else startScroll();
    };

    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(sync);
    };

    sync();
    window.addEventListener('resize', schedule);

    const headerComponent = document.querySelector('#header-component') || headerEl();
    if (headerComponent) {
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(schedule).observe(headerComponent);
      }
      new MutationObserver(schedule).observe(headerComponent, {
        attributes: true,
        attributeFilter: [
          'data-sticky-state',
          'data-mobile-search-open',
          'data-scroll-direction',
        ],
      });
    }

    document.addEventListener('product-display:updated', schedule);

    const requirementsDrawer = document.getElementById('mobile-drawer-requirements');
    if (requirementsDrawer) {
      new MutationObserver(schedule).observe(requirementsDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    document.querySelectorAll('dialog.requirements-dialog-element').forEach(dialog => {
      dialog.addEventListener('toggle', schedule);
    });
  }

  /**
   * Skip-to-content lands on the first field in the visible DDCS form.
   */
  bindSkipToCourseMaterials() {
    const skip = document.querySelector(
      'a.visually-hidden.button-secondary[href="#filters-section"], a.visually-hidden.button-secondary[href="#MainContent"]'
    );
    if (!skip || skip.dataset.cmrpSkipContentBound === 'true') return;
    skip.dataset.cmrpSkipContentBound = 'true';

    const FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const isVisible = el => {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      return el.getClientRects().length > 0;
    };

    const firstDdcsFocusable = () => {
      const root = document.getElementById('filters-section');
      if (!root) return null;

      const forms = root.querySelectorAll('.cmrp-filter-form');
      let searchRoot = root;
      for (let i = 0; i < forms.length; i += 1) {
        if (isVisible(forms[i])) {
          searchRoot = forms[i];
          break;
        }
      }

      const nodes = searchRoot.querySelectorAll(FOCUSABLE);
      for (let i = 0; i < nodes.length; i += 1) {
        const el = nodes[i];
        if (el.disabled) continue;
        if (el.tabIndex < 0) continue;
        if (!isVisible(el)) continue;
        if (el.closest('.cmrp-flow-switch')) continue;
        if (el.closest('.warning-message')) continue;
        if (el.classList.contains('cmrp-custom-dropdown__native')) continue;
        return el;
      }
      return root;
    };

    skip.addEventListener('click', event => {
      event.preventDefault();
      this.components.sidebar?.openSidePanel?.();

      const focusTarget = () => {
        const el = firstDdcsFocusable();
        if (!el) return;
        if (typeof el.focus === 'function') {
          el.focus();
        }
        el.scrollIntoView({ block: 'center', inline: 'nearest' });
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(focusTarget);
      });
    });
  }

  /**
   * Keep keyboard focus out from under the sticky Add to Bag bar.
   */
  bindStickyCtaFocusScroll() {
    document.addEventListener(
      'focusin',
      event => {
        const el = event.target;
        if (!(el instanceof HTMLElement)) return;
        if (!document.body.classList.contains('course-materials-page')) return;
        const results =
          document.getElementById('filtered-products') ||
          document.getElementById('products-section');
        if (!results || !results.contains(el)) return;
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      },
      true
    );
  }

  /**
   * Initialize all major components
   */
  initializeComponents() {
    // Initialize sidebar
    const SidebarClass = window.CourseMaterialsSidebar;
    if (typeof SidebarClass === 'undefined') {
      console.error('CourseMaterialsSidebar is not defined. Make sure course-materials-sidebar.js is loaded first.');
      return;
    }
    this.components.sidebar = new SidebarClass({
      ddcs_campus_entity: this.courseData.ddcs_campus_entity,
      ddcs_campus_division: this.courseData.ddcs_campus_division,
      ddcs_term: this.courseData.ddcs_term,
      ddcs_campus_department: this.courseData.ddcs_campus_department,
      ddcs_course: this.courseData.ddcs_course,
      ddcs_section: this.courseData.ddcs_section,
      selectedFilters: this.selectedFilters,
    });

    // Wait for dependencies then initialize SOLID system
    this.initializeSOLIDSystem();
  }

  /**
   * Initialize the SOLID system components
   */
  initializeSOLIDSystem() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo

    const checkDependencies = () => {
      attempts++;

      // Check missing dependencies
      const missing = [];
      if (!window.ProductSystem?.SOLIDProductHandler)
        missing.push('ProductSystem.SOLIDProductHandler');
      if (!window.ProductDisplayManager) missing.push('ProductDisplayManager');
      if (!window.SelectionManager) missing.push('SelectionManager');
      if (!window.NavigationUIManager) missing.push('NavigationUIManager');
      if (!window.URLStateManager) missing.push('URLStateManager');
      if (!window.MaterialInteractionManager)
        missing.push('MaterialInteractionManager');
      if (!window.UIStateManager) missing.push('UIStateManager');
      if (missing.length > 0) {
        if (attempts >= maxAttempts) {
          console.error(
            '[CMRP] Failed to load course materials dependencies:',
            missing
          );
          this.showSystemLoadFailure(
            CourseMaterialsPage.SYSTEM_LOAD_FAILURE_MESSAGE
          );
          return;
        }

        setTimeout(checkDependencies, 100);
        return;
      }

      try {
        this.setupSOLIDComponents();
        this.setupTemplateFunctions();
        // Reinstate last session results URL before URLStateManager parses params
        if (typeof window.CmrpSessionSnapshot?.tryRestoreResultsUrl === 'function') {
          window.CmrpSessionSnapshot.tryRestoreResultsUrl();
        }
        this.components.urlManager.initialize();
        this.components.uiStateManager.initialize();
        // Sticky ATB can survive flow switch with no course results to refresh;
        // hydrate from snapshot so the banner still shows.
        if (
          typeof window.CmrpSessionSnapshot?.hydrateSelectionManager ===
            'function' &&
          this.components.selectionManager
        ) {
          window.CmrpSessionSnapshot.hydrateSelectionManager(
            this.components.selectionManager
          );
          this.components.selectionManager.updateSelectionBarSummary?.();
        }
      } catch (error) {
        console.error('[CMRP] Error during system initialization:', error);
        this.showSystemLoadFailure(
          'Error during system initialization. Please refresh the page.'
        );
      }
    };

    checkDependencies();
  }

  /**
   * Student ID flow is the visible form. Session storage is only a fallback
   * when the filters panel has not been marked yet.
   * @returns {boolean}
   */
  isStudentIdFlow() {
    const filters = document.getElementById('filters-section');
    if (filters) return filters.classList.contains('student-id-mode');
    try {
      return sessionStorage.getItem('cmrp_flow') === 'student-id';
    } catch (e) {
      return false;
    }
  }

  /**
   * Script-load failure banner. The dependency-timeout message redirects from
   * Student ID into course builder, then renders there. On course builder it
   * renders in place. Other failures stay on the visible form.
   * @param {string} message
   */
  showSystemLoadFailure(message) {
    const loadingEl = document.getElementById('loading-state');
    if (loadingEl) loadingEl.style.display = 'none';

    const placeholder = document.getElementById('initial-placeholder');
    if (placeholder) placeholder.style.display = 'none';

    const isSystemLoad =
      message === CourseMaterialsPage.SYSTEM_LOAD_FAILURE_MESSAGE;
    if (isSystemLoad && this.isStudentIdFlow()) {
      this.redirectToCourseBuilderWithError(
        CourseMaterialsPage.SYSTEM_LOAD_ERROR_CODE
      );
      return;
    }
    if (isSystemLoad) {
      this.showCourseBuilderError(message);
      this.bindCourseBuilderErrorDismissOnInteract();
      return;
    }

    const warningIds = this.getVisibleCmrpErrorBannerIds();
    this.showErrorMessage(message, '', false, { warningIds });
  }

  /**
   * Single error banner id for the currently visible CMRP form.
   * Student ID banners live inside a form that is `display: none` on course-builder,
   * so defaulting to them makes system-load errors invisible.
   * Returns at most one id so startup failures do not render twice.
   * @returns {string[]}
   */
  getVisibleCmrpErrorBannerIds() {
    const id =
      typeof window.cmrpId === 'function'
        ? window.cmrpId.bind(window)
        : /** @param {string} base */ base => base;

    const filters = document.getElementById('filters-section');
    const onStudentId = Boolean(filters?.classList.contains('student-id-mode'));

    // Prefer one banner for the active flow. Do not fan out to SSO + student +
    // mobile slots — that duplicates the same startup-failure message.
    const preferred = onStudentId
      ? [
          id('student-id-error-warning', 'sid'),
          'student-id-error-warning',
          'sso-student-id-error-warning',
        ]
      : [id('course-builder-error-warning', 'cb'), 'course-builder-error-warning'];

    const first = preferred.find(warningId =>
      Boolean(document.getElementById(warningId))
    );
    return first ? [first] : [];
  }

  updateSOLIDComponents() {
    // Product Handler
    this.components.productHandler =
      window.ProductSystem.SOLIDProductHandler.createForShopify(
        this.courseData.products_Materials,
        this.courseData.ddcs_section
      );

    // Display Manager
    this.components.displayManager = new window.ProductDisplayManager({
      products: this.courseData.products_Materials,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      productHandler: this.components.productHandler,
    });

    // Selection Manager
    this.components.selectionManager = new window.SelectionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
    });

    // Navigation Manager
    this.components.navigationManager = new window.NavigationUIManager({
      selectedFilters: this.selectedFilters,
      products: this.courseData.products_Materials,
    });

    // URL Manager
    this.components.urlManager = new window.URLStateManager({
      selectedFilters: this.selectedFilters,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      ddcsCampusDepartment: this.courseData.ddcs_campus_department,
      ddcsCourse: this.courseData.ddcs_course,
      addLabelCallback: objCourse => this.addLabel(objCourse),
      refreshProductsCallback: () => window.refreshProductsDisplay(),
    });

    // Interaction Manager
    this.components.interactionManager = new window.MaterialInteractionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
      updateSelectedProductsCallback: () =>
        this.components.selectionManager.updateSelectedProducts(),
      updateSelectionBarSummaryCallback: () =>
        this.components.selectionManager.updateSelectionBarSummary(),
    });

    // UI State Manager
    this.components.uiStateManager = new window.UIStateManager();

    // Expose global functions for backward compatibility
    this.exposeGlobalFunctions();
  }
  /**
   * Setup SOLID architecture components
   */
  setupSOLIDComponents() {
    // Product Handler
    this.components.productHandler =
      window.ProductSystem.SOLIDProductHandler.createForShopify(
        this.courseData.products_Materials,
        this.courseData.ddcs_section
      );

    // Display Manager
    this.components.displayManager = new window.ProductDisplayManager({
      products: this.courseData.products_Materials,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      productHandler: this.components.productHandler,
    });

    // Selection Manager
    this.components.selectionManager = new window.SelectionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
    });

    // Navigation Manager
    this.components.navigationManager = new window.NavigationUIManager({
      selectedFilters: this.selectedFilters,
      products: this.courseData.products_Materials,
    });

    // URL Manager
    this.components.urlManager = new window.URLStateManager({
      selectedFilters: this.selectedFilters,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      ddcsCampusDepartment: this.courseData.ddcs_campus_department,
      ddcsCourse: this.courseData.ddcs_course,
      addLabelCallback: objCourse => this.addLabel(objCourse),
      refreshProductsCallback: () => window.refreshProductsDisplay(),
    });

    // Interaction Manager
    this.components.interactionManager = new window.MaterialInteractionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
      updateSelectedProductsCallback: () =>
        this.components.selectionManager.updateSelectedProducts(),
      updateSelectionBarSummaryCallback: () =>
        this.components.selectionManager.updateSelectionBarSummary(),
    });

    // UI State Manager
    this.components.uiStateManager = new window.UIStateManager();

    // Expose global functions for backward compatibility
    this.exposeGlobalFunctions();
  }

  /**
   * Setup template functions for backward compatibility
   */
  setupTemplateFunctions() {
    const handler = this.components.productHandler;

    // Product handler functions
    window.generateFormatButtons = handler.generateFormatButtons.bind(handler);
    window.generateInitialPurchaseOptions =
      handler.generateInitialPurchaseOptions.bind(handler);
    window.extractAuthorName = handler.extractAuthorName.bind(handler);
    window.extractEditionFromTitle =
      handler.extractEditionFromTitle.bind(handler);
    window.extractISBNFromProduct =
      handler.extractISBNFromProduct.bind(handler);
    window.extractPublisherName = handler.extractPublisherName.bind(handler);
    window.extractPublishedDate = handler.extractPublishedDate.bind(handler);

    window.extractPublisher = handler.extractPublisher.bind(handler);
    window.extractFormat = handler.extractFormat.bind(handler);
    window.extractCopyRight = handler.extractCopyRight.bind(handler);
    window.extractPublication = handler.extractPublication.bind(handler);

    window.getLowestPriceForFormat =
      handler.getLowestPriceForFormat.bind(handler);
    window.getSelectedFormat = handler.getSelectedFormat.bind(handler);
    window.findVariant = handler.findVariant.bind(handler);

    // Utility functions
    window.extractPublisherCode = product =>
      handler.extractPublisherName(product);
    window.extractMTCTitleId = product =>
      product.id?.toString() || 'Not available';
    window.formatDate = dateString => {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch (e) {
        return dateString;
      }
    };
    window.getVariantByOptions = (product, format, type, condition = 'New') => {
      return handler.priceCalculator.getVariantByOptions(product, format, type);
    };
    window.getCheckboxData = checkbox => {
      const materialItem = checkbox.closest('.material-item');
      const selectedFormat =
        (checkbox.dataset.format
          ? String(checkbox.dataset.format).toLowerCase()
          : null) ||
        window.courseMaterialsPage?.components?.selectionManager?.getSelectedFormat?.(
          materialItem
        ) ||
        handler.getSelectedFormat(materialItem);

      // Check if this is a simple product (determined by html-renderer based on variant data)
      const formatBtns = materialItem?.querySelectorAll('.format-btn') || [];
      const isSimple = formatBtns.length === 0;

      return {
        productId: checkbox.dataset.productId,
        selectedFormat: selectedFormat || (isSimple ? 'simple' : null),
        purchaseType: checkbox.dataset.purchaseType,
        productTitle: checkbox.dataset.productTitle,
        isSimpleProduct: isSimple,
      };
    };

    // Interaction manager functions
    window.handleFormatSelection =
      this.components.interactionManager.handleFormatSelection;
    window.updatePricingForFormat =
      this.components.interactionManager.updatePricingForFormat;
    window.togglePurchaseOption = (element, event) => {
      try {
        return this.components.interactionManager.togglePurchaseOption(
          element,
          event
        );
      } catch (error) {
        // Silent error handling
      }
    };
    window.handlePurchaseSelection = element => {
      try {
        return this.components.interactionManager.handlePurchaseSelection(
          element
        );
      } catch (error) {
        // Silent error handling
      }
    };
    window.updateSelectedProducts =
      this.components.selectionManager.updateSelectedProducts;
    window.updateSelectionBarSummary =
      this.components.selectionManager.updateSelectionBarSummary;
    window.addMaterialEventListeners =
      this.components.interactionManager.addMaterialEventListeners;
    window.toggleViewMore = this.components.interactionManager.toggleViewMore;
    window.populateExpandedDetails =
      this.components.interactionManager.populateExpandedDetails;
    window.getSelectedFormat =
      this.components.interactionManager.getSelectedFormat;
    window.getVariantByOptions =
      this.components.interactionManager.getVariantByOptions;

    // Navigation manager functions
    window.generateDepartmentChips =
      this.components.navigationManager.generateDepartmentChips;
    window.filterProductsByDepartment =
      this.components.navigationManager.filterProductsByDepartment;
    window.updateMaterialsCount =
      this.components.navigationManager.updateMaterialsCount;
    window.updateCampusTermDisplay =
      this.components.navigationManager.updateCampusTermDisplay;
    window.initializeDepartmentFilters =
      this.components.navigationManager.initializeDepartmentFilters;

    // URL manager functions
    window.setURLSearchParams = this.components.urlManager.setURLSearchParams;
    window.restoreFormStateFromURL =
      this.components.urlManager.restoreFormStateFromURL;
    window.buildFiltersFromURLParams =
      this.components.urlManager.buildFiltersFromURLParams;
    window.findMatchingEntities =
      this.components.urlManager.findMatchingEntities;
    window.createFilterObject = this.components.urlManager.createFilterObject;
    window.restoreDropdownsAndUI =
      this.components.urlManager.restoreDropdownsAndUI;
    window.setCampusDropdown = this.components.urlManager.setCampusDropdown;
    window.setTermDropdown = this.components.urlManager.setTermDropdown;
    window.populateTermsForCampus =
      this.components.urlManager.populateTermsForCampus;
    window.restoreAllFormFieldsFromURL =
      this.components.urlManager.restoreAllFormFieldsFromURL;
    window.restoreFieldsCascade =
      this.components.urlManager.restoreFieldsCascade;
    window.triggerDepartmentCascadeForRestore =
      this.components.urlManager.triggerDepartmentCascadeForRestore;
    window.attemptURLRestoration =
      this.components.urlManager.attemptURLRestoration;

    // UI state manager functions
    window.showLoadingState = this.components.uiStateManager.showLoadingState;
    window.hideLoadingState = this.components.uiStateManager.hideLoadingState;
    window.showEmptyState = this.components.uiStateManager.showEmptyState;
    window.showEmptyStateWithAdoption =
      this.components.uiStateManager.showEmptyStateWithAdoption;
    window.hideEmptyState = this.components.uiStateManager.hideEmptyState;
    window.createInlineLoader =
      this.components.uiStateManager.createInlineLoader;
    window.removeInlineLoader =
      this.components.uiStateManager.removeInlineLoader;
  }

  /**
   * Expose global functions for the page
   */
  exposeGlobalFunctions() {
    window.courseMaterialsInteractionManager =
      this.components.interactionManager;
    window.addSelectedProductsToCart =
      this.components.selectionManager.addSelectedProductsToCart;
    window.handleNotifyMe = this.components.productHandler.handleNotifyMe;

    window.refreshProductsDisplay = async () => {
      this.components.uiStateManager.showLoadingState();
      try {
        // Nav must see updated filters before generateDepartmentChips runs inside the display pass
        this.components.navigationManager.updateSelectedFilters(
          this.selectedFilters
        );
        await this.components.displayManager.refreshProductsDisplay(
          this.selectedFilters
        );
        this.components.navigationManager.updateSelectedFilters(
          this.selectedFilters
        );
        this.components.navigationManager.refreshNavigation();
        // Session snapshot → in-memory selections, then existing DOM rehydrate
        if (
          typeof window.CmrpSessionSnapshot?.hydrateSelectionManager ===
          'function'
        ) {
          window.CmrpSessionSnapshot.hydrateSelectionManager(
            this.components.selectionManager
          );
        }
        this.components.selectionManager.rehydrateCheckboxSelections();
        // Rebuild from checked boxes so live cards get full variant + cart props;
        // orphaned snapshot rows (section no longer rendered) stay preserved.
        this.components.selectionManager.updateSelectedProducts();
        this.components.selectionManager.updateSelectionBarSummary();
      } catch (error) {
        console.error('refreshProductsDisplay failed:', error);
        this.components.uiStateManager.hideLoadingState();
      }
    };

    // Store reference to this instance
    window.courseMaterialsPage = this;
    window.courseMaterialsSidebar = this.components.sidebar;

    // Expose utility functions
    window.removeCourse = element => {
      try {
        return this.removeCourse(element);
      } catch (error) {
        console.error('removeCourse failed:', error);
      }
    };

    window.removeCourseSelection = (department, course, section, division) => {
      try {
        return this.removeCourseSelection(
          department,
          course,
          section,
          division,
        );
      } catch (error) {
        console.error('removeCourseSelection failed:', error);
      }
    };
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Sidebar events
    document.addEventListener('sidebar:courseAdded', event => {
      const detail = event.detail || {};
      const { course, suppressRefresh, suppressUrlUpdate } = detail;

      if (course == null) {
        return;
      }

      // Use the new method to prevent duplicates
      const wasAdded = this.addCourseToFilters(course);

      if (wasAdded) {
        window.CmrpSessionSnapshot?.adoptIfForeignFlow?.();
        if (!suppressUrlUpdate) {
          this.components.urlManager.setURLSearchParams();
        }
        this.addLabel(course);
        if (!suppressRefresh) {
          window.refreshProductsDisplay();
        }
      }
    });

    document.addEventListener('sidebar:studentSubmitted', event => {
      const { studentData, onComplete, onError } = event.detail;
      this.handleStudentSubmission(studentData, onComplete, onError);
    });

    // Listen for mobile student ID submission
    document.addEventListener('mobile:studentIdSubmitted', event => {
      const { studentData, onComplete, onError } = event.detail;
      this.handleStudentSubmission(studentData, onComplete, onError, {
        closeMobileDrawerOnlyOnSuccess: true,
      });
    });

    document.addEventListener('sidebar:clearSelections', event => {
      const { clearCampus, clearTerm } = event.detail;
      this.clearAllUserSelections(clearCampus, clearTerm);
    });

    document.addEventListener('sidebar:clearAllTermSelections', event => {
      const { clearTerm } = event.detail;
      this.clearAllTermSelections(clearTerm);
    });

    // Navigation manager initialization on DOM ready
    document.addEventListener('DOMContentLoaded', () => {
      if (this.components.navigationManager) {
        this.components.navigationManager.initializeOnDOMReady();
      } else {
        // Fallback for when navigation manager isn't ready yet
        const navPanel = document.getElementById('right-panel-nav');
        const campusTermBar = document.getElementById('campus-term-info');

        if (navPanel) navPanel.style.display = 'none';
        if (campusTermBar) campusTermBar.style.display = 'none';
      }
    });
  }
  parseURLParameters() {
    
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      // @ts-ignore
      shopBy: urlParams.get(this.URL_PARAMS.SHOP_BY),
      // @ts-ignore
      termId: urlParams.get(this.URL_PARAMS.TERM_ID),
      // @ts-ignore
      divisionDisplayName: urlParams.get(this.URL_PARAMS.DIVISION_DISPLAY_NAME),
      // @ts-ignore
      departmentDisplayName: urlParams.get(
        this.URL_PARAMS.DEPARTMENT_DISPLAY_NAME
      ),
      // @ts-ignore
      courseDisplayName: urlParams.get(this.URL_PARAMS.COURSE_DISPLAY_NAME),
      // @ts-ignore
      courseRefId: urlParams.get(this.URL_PARAMS.COURSE_REF_ID),
      // @ts-ignore
      sectionDisplayName: urlParams.get(this.URL_PARAMS.SECTION_DISPLAY_NAME),
      // @ts-ignore
      
    };

    const parsed = {
      termsIds: params.termId && params.sectionDisplayName     ? Array(params.sectionDisplayName.split('|').length).fill(params.termId)     : [],
      divisions: params.divisionDisplayName
        ? params.divisionDisplayName.split('|')
        : [],
      departments: params.departmentDisplayName
        ? params.departmentDisplayName.split('|')
        : [],
      courses: params.courseDisplayName
        ? params.courseDisplayName.split('|')
        : [],
      courseRefIds: params.courseRefId
        ? params.courseRefId.split('|')
        : [],
      sections: params.sectionDisplayName
        ? params.sectionDisplayName.split('|')
        : [],
    };
    const needsTermIdsForCourseRef =
      parsed.termsIds.length === 0 &&
      Boolean(params.termId) &&
      parsed.courseRefIds.length > 0;
    if (needsTermIdsForCourseRef) {
      parsed.termsIds = Array(parsed.courseRefIds.length).fill(params.termId);
    }
       
    return parsed;
  }
  /**
   * Initialize UI state
   */
  initializeUI() {
    // Only show initial loading when opening the page with URL params
    setTimeout(() => {
      const hasURLParams = window.location.search.length > 1;
      const { termsIds, divisions, departments, courses, sections } =
      this.parseURLParameters();
      const hasURLParamsToRestore = [
        termsIds,
        divisions,
        departments,
        courses,
        sections,
      ].some(arr => arr.length > 0);  
      const shouldSkipInitialLoading = window.__skipInitialCourseLoading === true;
      if (this.components.uiStateManager && hasURLParams && hasURLParamsToRestore && !shouldSkipInitialLoading) {
        this.components.uiStateManager.showLoadingState();
      }
    }, 50);
  }

  /**
   * Handle desktop sidebar auto-opening
   */
  handleDesktopSidebar() {
    setTimeout(() => {
      if (
        document.body.classList.contains('course-materials-page') ||
        window.innerWidth > 968
      ) {
        this.components.sidebar?.openSidePanel?.();
      }
    }, 100);
  }

  /**
   * Add a course label to the UI
   */
  addLabel(objCourse) {
    const courseListElement = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (!courseListElement) return;

    if (
      objCourse.selectedCampus &&
      objCourse.selectedTerm &&
      objCourse.selectedDepartment &&
      objCourse.selectedcourse &&
      objCourse.selectedsection
    ) {
      const courseHTML = `
                <div class="course-item" data-campus="${
                  objCourse.selectedCampus
                }" 
                     data-term="${objCourse.selectedTerm}" data-division="${
        objCourse.selectedDivision || ''
      }" data-department="${
        objCourse.selectedDepartment
      }" 
                     data-course="${objCourse.selectedcourse}" data-section="${
        objCourse.selectedsection
      }"
                     ${
                       objCourse.fromStudentId
                         ? 'data-from-student-id="true"'
                         : ''
                     }>
                    ${
                      objCourse.selectedDivision
                        ? `${objCourse.selectedDivision} | `
                        : ''
                    }${objCourse.selectedDepartment} ${
        objCourse.selectedcourse
      } | ${objCourse.selectedsection} 
                    <span class="remove-course">×</span>
                </div>`;

      courseListElement.insertAdjacentHTML('beforeend', courseHTML);

      // Show the form actions container when first course is added
      if (formActionsContainer && courseListElement.children.length === 1) {
        formActionsContainer.style.display = 'flex';
      }

      const newCourseItem = courseListElement.lastElementChild;
      const removeButton = newCourseItem.querySelector('.remove-course');

      if (removeButton) {
        removeButton.addEventListener('click', () => {
          this.removeCourse(newCourseItem);
        });
      }
    }
  }
  /**
   * Normalize course identity fields for comparisons.
   * @param {unknown} v
   * @returns {string}
   */
  static _trimCourseField(v) {
    return v == null || v === '' ? '' : String(v).trim();
  }

  /** Loose compare for URL-restored vs DOM labels (spaces, underscores, case). */
  static _looseCourseFieldEq(a, b) {
    const n = v =>
      String(v ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
    return n(a) === n(b);
  }

  /**
   * Single entry point: remove one course by department + course number + section (+ optional division).
   * Updates selectedFilters, clears bubbles/tags, re-fetches/renders products via refreshProductsDisplay.
   * If the course is only a pending mobile tag (not yet in selectedFilters), only clears that UI.
   * When division is provided, only that grade/division line is removed.
   * Blank/missing division is treated as unspecified so adoption / not-found cards
   * that omit `data-division` still match a filter that has a division name.
   * @param {unknown} department
   * @param {unknown} course
   * @param {unknown} section
   * @param {unknown} [division]
   * @param {{ sectionId?: string, sectionGid?: string, allowLastFilterFallback?: boolean }} [extra]
   */
  removeCourseSelection(department, course, section, division, extra = {}) {
    const t = CourseMaterialsPage._trimCourseField;
    const looseEq = CourseMaterialsPage._looseCourseFieldEq;
    const d = t(department);
    const c = t(course);
    const s = t(section);
    const div = t(division);
    const divProvided = Boolean(div);
    const sectionId = t(extra?.sectionId || extra?.selectedsectionId);
    const sectionGid = t(extra?.sectionGid || extra?.selectedsectionGid);
    const allowLastFilterFallback = extra?.allowLastFilterFallback === true;
    if (!sectionId && !sectionGid && (!c || !s)) {
      return;
    }

    const matchDivision = f => {
      if (!divProvided) return true;
      const fdiv = t(f.selectedDivision ?? f.selecteddivision ?? '');
      return fdiv === div || looseEq(fdiv, div);
    };

    const matchByIds = f => {
      if (sectionId) {
        const fid = t(f.selectedsectionId ?? f.section_id ?? '');
        if (fid && (fid === sectionId || looseEq(fid, sectionId))) return true;
      }
      if (sectionGid) {
        const fgid = t(f.selectedsectionGid ?? '');
        if (fgid && (fgid === sectionGid || looseEq(fgid, sectionGid))) {
          return true;
        }
      }
      return false;
    };

    const matchTripleExact = f => {
      const fd = t(f.selectedDepartment ?? f.selecteddepartment);
      const fc = t(f.selectedcourse ?? f.course);
      const fs = t(f.selectedsection ?? f.section);
      return fd === d && fc === c && fs === s && matchDivision(f);
    };

    const matchTripleLoose = f =>
      (d ? looseEq(f.selectedDepartment ?? f.selecteddepartment, d) : true) &&
      looseEq(f.selectedcourse ?? f.course, c) &&
      looseEq(f.selectedsection ?? f.section, s) &&
      matchDivision(f);

    let filterIndex = sectionId || sectionGid
      ? this.selectedFilters.findIndex(matchByIds)
      : -1;
    if (filterIndex === -1 && d && c && s) {
      filterIndex = this.selectedFilters.findIndex(matchTripleExact);
    }
    if (filterIndex === -1 && c && s) {
      filterIndex = this.selectedFilters.findIndex(matchTripleLoose);
    }
    if (filterIndex === -1 && c && s) {
      const courseSectionHits = [];
      this.selectedFilters.forEach((f, i) => {
        if (
          looseEq(f.selectedcourse ?? f.course, c) &&
          looseEq(f.selectedsection ?? f.section, s)
        ) {
          courseSectionHits.push(i);
        }
      });
      if (courseSectionHits.length === 1) {
        filterIndex = courseSectionHits[0];
      }
    }
    if (
      filterIndex === -1 &&
      allowLastFilterFallback &&
      this.selectedFilters.length === 1
    ) {
      filterIndex = 0;
    }
    if (filterIndex === -1) {
      window.courseMaterialsSidebar?.clearCourseSelectionUI?.(d, c, s, div);
      void window.courseMaterialsSidebar?.resetCourseBuilderLineKeepCampusTerm?.();
      window.courseMaterialsMobile?.updateMobileSubmitButtonState?.();
      if (!document.querySelector('#mobile-selected-courses .mobile-course-tag')) {
        window.courseMaterialsMobile?.handleEmptyCoursesState?.();
      }
      window.courseMaterialsMobile?.updateRightPanelContent?.();
      return;
    }

    const removedFilter = this.selectedFilters[filterIndex];

    this.selectedFilters.splice(filterIndex, 1);

    if (typeof this.components.urlManager?.updateSelectedFilters === 'function') {
      this.components.urlManager.updateSelectedFilters(this.selectedFilters);
    }

    // Retain ATB selections when clearing a CMRP section (do not prune selectedProducts).

    const removedDiv = t(
      removedFilter.selectedDivision ?? removedFilter.selecteddivision ?? '',
    );
    this.removeCourseListItemIfPresent(d, c, s, removedDiv);
    this.updateCourseListFormVisibility();

    const canonD = t(
      removedFilter.selectedDepartment ?? removedFilter.selecteddepartment
    );
    const canonC = t(removedFilter.selectedcourse ?? removedFilter.course);
    const canonS = t(removedFilter.selectedsection ?? removedFilter.section);
    window.courseMaterialsSidebar?.clearCourseSelectionUI?.(
      canonD,
      canonC,
      canonS,
      removedDiv,
    );
    window.courseMaterialsSidebar?.clearCourseSelectionUI?.(d, c, s, removedDiv);

    document.dispatchEvent(
      new CustomEvent('sidebar:courseRemoved', {
        detail: {
          course: {
            selectedDepartment: d,
            selectedcourse: c,
            selectedsection: s,
            selectedDivision: removedDiv,
            selectedCampus:
              removedFilter.selectedCampus ?? removedFilter.selectedcampus,
            selectedTerm: removedFilter.selectedTerm ?? removedFilter.selectedterm,
          },
        },
      })
    );

    this.components.urlManager.setURLSearchParams({ forceEmptyStrip: true });

    // Keep the stored results URL in sync so hydrate does not restore the
    // deleted course. Last remaining card: drop the URL, keep ATB selections.
    // Prefer the live address bar (just rewritten from remaining filters) over
    // stripping the previous snapshot URL by display-name, which can miss.
    if (typeof window.CmrpSessionSnapshot === 'object' && window.CmrpSessionSnapshot) {
      if (this.selectedFilters.length === 0) {
        window.CmrpSessionSnapshot.clearLastResultsUrl?.();
      } else {
        window.CmrpSessionSnapshot.saveResultsUrl?.(
          `${window.location.pathname}${window.location.search}`
        );
      }
    }

    void (async () => {
      const stillHasCourses = this.selectedFilters.length > 0;
      try {
        if (stillHasCourses) {
          await window.courseMaterialsSidebar?.resetCourseBuilderLineKeepCampusTerm?.();
        }
        await window.refreshProductsDisplay?.();
      } catch (e) {
        console.error('[CMRP remove] refreshProductsDisplay failed', e);
      }
      if (this.selectedFilters.length === 0) {
        this.handleEmptySelections();
        this.removeAllMobileTags();
        this.components.navigationManager?.hideNavigationPanels?.();
      }
      window.courseMaterialsMobile?.updateMobileSubmitButtonState?.();
    })();
  }

  removeCourseListItemIfPresent(d, c, s, division) {
    const t = CourseMaterialsPage._trimCourseField;
    const list = document.getElementById('course-list');
    if (!list) return;
    const divProvided = Boolean(t(division));
    const div = divProvided ? t(division) : null;
    list.querySelectorAll('.course-item').forEach(el => {
      if (
        t(el.getAttribute('data-department')) === d &&
        t(el.getAttribute('data-course')) === c &&
        t(el.getAttribute('data-section')) === s &&
        (!divProvided || t(el.getAttribute('data-division') || '') === div)
      ) {
        el.remove();
      }
    });
  }

  updateCourseListFormVisibility() {
    const courseList = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (
      courseList &&
      formActionsContainer &&
      courseList.children.length === 0
    ) {
      formActionsContainer.style.display = 'none';
    }
  }

  /**
   * Legacy hook: in-card remove buttons and course-list items use data-* attrs.
   * @param {Element} courseElement
   */
  removeCourse(courseElement) {
    if (!courseElement?.getAttribute) return;
    const t = CourseMaterialsPage._trimCourseField;
    const rawDiv = courseElement.getAttribute('data-division');
    this.removeCourseSelection(
      t(courseElement.getAttribute('data-department')),
      t(courseElement.getAttribute('data-course')),
      t(courseElement.getAttribute('data-section')),
      rawDiv,
      {
        sectionId: t(
          courseElement.getAttribute('data-section-id') ||
            courseElement.getAttribute('data-selectedsection-id')
        ),
        sectionGid: t(courseElement.getAttribute('data-section-gid')),
        allowLastFilterFallback: true,
      }
    );
  }
  
  /**
   * Remove all mobile course tags and show empty state
   */
  removeAllMobileTags() {
    const selectedCoursesContainer = document.getElementById('mobile-selected-courses');
    if (selectedCoursesContainer) {
      // Remove all tags
      const allTags = selectedCoursesContainer.querySelectorAll('.mobile-course-tag');
      allTags.forEach(tag => tag.remove());
      
      // Hide container
      selectedCoursesContainer.style.display = 'none';
      
      // Handle empty state
      if (window.courseMaterialsMobile) {
        window.courseMaterialsMobile.handleEmptyCoursesState();
        window.courseMaterialsMobile.updateMobileSubmitButtonState();
      }
    }
  }

  /**
   * Handle empty selections state
   */
  handleEmptySelections() {
    this.components.uiStateManager.showEmptyState();
    this.components.navigationManager?.hideNavigationPanels?.();

    const campusSelect =
      document.getElementById('campus-cb') ||
      (typeof window.cmrpQueryDesktopCampusTerm === 'function'
        ? window.cmrpQueryDesktopCampusTerm('campus')
        : document.querySelector('#campus-term-dropdowns #campus'));
    const termSelect =
      document.getElementById('term-cb') ||
      (typeof window.cmrpQueryDesktopCampusTerm === 'function'
        ? window.cmrpQueryDesktopCampusTerm('term')
        : document.querySelector('#campus-term-dropdowns #term'));

    if (campusSelect && campusSelect.value) {
      if (termSelect && !termSelect.value) {
        this.components.sidebar.populateTerms(campusSelect);
      } else if (termSelect && termSelect.value) {
        //this.components.sidebar.clearDDCSSelections(true);
      }
    } else {
      this.components.sidebar.populateCampus();
    }
  }

  /**
   * Test API connectivity
   */
  async testAPIConnectivity(baseUrl) {
    try {
      // Try a simple HEAD request first to test connectivity
      const testResponse = await fetch(baseUrl, {
        method: 'HEAD',
        mode: 'no-cors', // This helps avoid CORS preflight issues
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Show error message in sidebar and mobile-drawer warning components (same markup as `warning-message` snippet).
   */
  showErrorMessage(message, supportingMessage = '', hasButton = false, options = {}) {
    const { noCloseWhenButton = false, warningIds = null } = options;
    const ids =
      Array.isArray(warningIds) && warningIds.length > 0
        ? warningIds
        : [this.warningId, this.mobileWarningId];

    ids.forEach(warningId => {
      const warningElement = document.getElementById(warningId);
      if (!warningElement) return;

      if (
        hasButton &&
        (message === 'This is taking longer than expected' ||
          message === 'This is taking longer than expected.' ||
          noCloseWhenButton)
      ) {
        warningElement.classList.add('warning-message--no-close');
      } else {
        warningElement.classList.remove('warning-message--no-close');
      }

      const mainMessageElement = warningElement.querySelector(
        '.warning-message__text--main'
      );
      if (mainMessageElement) mainMessageElement.textContent = message;

      const supportingMessageElement = warningElement.querySelector(
        '.warning-message__text--supporting'
      );
      if (supportingMessageElement) {
        if (supportingMessage) {
          if (hasButton) {
            const buttonText = 'Add courses manually';
            const messageWithButton = supportingMessage.replace(
              buttonText,
              `<button class="warning-message__inline-btn" onclick="window.courseMaterialsSidebar?.switchToManualCourseSelection()">${buttonText}</button>`
            );
            supportingMessageElement.innerHTML = messageWithButton;
          } else {
            supportingMessageElement.textContent = supportingMessage;
          }
          supportingMessageElement.style.display = 'block';
        } else {
          supportingMessageElement.style.display = 'none';
        }
      }

      const legacyMessageElement = warningElement.querySelector(
        '.warning-message__text:not(.warning-message__text--main):not(.warning-message__text--supporting)'
      );
      if (legacyMessageElement && !mainMessageElement) {
        legacyMessageElement.textContent = message;
      }

      if (typeof window.showWarningMessage === 'function') {
        window.showWarningMessage(warningId);
      } else {
        warningElement.style.display = 'block';
      }
    });
  }

  /**
   * Hide error message (desktop sidebar + mobile drawer)
   */
  hideErrorMessage() {
    [this.warningId, this.mobileWarningId].forEach(warningId => {
      const warningElement = document.getElementById(warningId);
      if (warningElement) {
        warningElement.classList.remove('warning-message--no-close');
      }
      if (typeof window.hideWarningMessage === 'function') {
        window.hideWarningMessage(warningId);
      } else if (warningElement) {
        warningElement.style.display = 'none';
      }
    });
  }

  /**
   * Course-builder error banner ids (validation + redirected Student ID discovery errors).
   * @returns {string[]}
   */
  getCourseBuilderWarningIds() {
    const id =
      typeof window.cmrpId === 'function'
        ? window.cmrpId.bind(window)
        : /** @param {string} base */ base => base;
    const candidates = [
      id('course-builder-error-warning', 'cb'),
      'course-builder-error-warning',
    ];
    return [...new Set(candidates)].filter(warningId =>
      Boolean(document.getElementById(warningId))
    );
  }

  /**
   * Hide course-builder error banner(s).
   */
  hideCourseBuilderErrorMessage() {
    this.getCourseBuilderWarningIds().forEach(warningId => {
      const warningElement = document.getElementById(warningId);
      if (warningElement) {
        warningElement.classList.remove('warning-message--no-close');
      }
      if (typeof window.hideWarningMessage === 'function') {
        window.hideWarningMessage(warningId);
      } else if (warningElement) {
        warningElement.style.display = 'none';
      }
    });
  }

  /**
   * Map ConnectOnce / HTTP discovery failure codes to course-builder banner copy.
   * @param {string|null} code
   * @returns {string}
   */
  getStudentIdDiscoveryErrorMessage(code) {
    // Retry group (007 / 008 / 009 / 500) — after retry fails
    if (['007', '008', '009', '500'].includes(code)) {
      return 'This is taking longer than expected. Please select your courses below to shop by course.';
    }
    // Immediate redirect (003 / 004 / 005 / 006 / 010) and unknown
    return 'Sorry we are having trouble pulling your details right now. Please select your courses below to shop by course.';
  }

  /**
   * Redirect to course builder with `sidError=<code>` in the URL (one-shot banner handoff).
   * @param {string|null} code - Three-digit normalized ConnectOnce code, or null
   */
  redirectToCourseBuilderWithError(code) {
    const normalized =
      this.normalizeStudentDiscoveryStatusCode(code) ||
      (code == null || code === ''
        ? 'unknown'
        : code === '500'
          ? '500'
          : String(code).padStart(3, '0'));

    if (typeof window.cmrpPersistFlow === 'function') {
      window.cmrpPersistFlow('course-builder');
    } else {
      try {
        sessionStorage.setItem('cmrp_flow', 'course-builder');
      } catch (e) {
        /* ignore */
      }
    }
    try {
      sessionStorage.removeItem('sessionToken');
    } catch (e) {
      /* ignore */
    }

    const params = new URLSearchParams(window.location.search);
    // Drop stale results / flow deep-links; keep unrelated params
    [
      'shopBy',
      'termId',
      'divisionDisplayName',
      'departmentDisplayName',
      'courseDisplayName',
      'courseRefId',
      'sectionDisplayName',
      'campus',
      'campusId',
      'term',
      'student_id',
      'code',
      CourseMaterialsPage.SID_ERROR_PARAM,
    ].forEach(key => params.delete(key));

    params.set(CourseMaterialsPage.SID_ERROR_PARAM, normalized);
    params.set('shopBy', 'course');

    const qs = params.toString();
    window.location.assign(
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    );
  }

  /**
   * Show copy in the course-builder form banner (same element as DDCS validation).
   * @param {string} message
   */
  showCourseBuilderError(message) {
    const warningIds = this.getCourseBuilderWarningIds();
    if (warningIds.length === 0) return;
    this.showErrorMessage(String(message || ''), '', false, { warningIds });
  }

  /**
   * Read `sidError` from the URL, show the course-builder banner, then strip the param.
   */
  consumePendingCourseBuilderError() {
    const params = new URLSearchParams(window.location.search);
    const rawCode = params.get(CourseMaterialsPage.SID_ERROR_PARAM);
    if (rawCode == null || rawCode === '') return;

    // Strip immediately so refresh / share / back doesn't re-flash the banner
    params.delete(CourseMaterialsPage.SID_ERROR_PARAM);
    const qs = params.toString();
    const cleanUrl = qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    if (rawCode === CourseMaterialsPage.SYSTEM_LOAD_ERROR_CODE) {
      this.showCourseBuilderError(CourseMaterialsPage.SYSTEM_LOAD_FAILURE_MESSAGE);
      this.bindCourseBuilderErrorDismissOnInteract();
      return;
    }

    const code =
      rawCode === 'unknown'
        ? null
        : rawCode === '500'
          ? '500'
          : this.normalizeStudentDiscoveryStatusCode(rawCode) || rawCode;
    this.showCourseBuilderError(this.getStudentIdDiscoveryErrorMessage(code));
    this.bindCourseBuilderErrorDismissOnInteract();
  }

  /**
   * Hide the course-builder banner once, when the user starts editing the form.
   * One-shot so later DDCS validation on the same banner is not cleared by this listener.
   */
  bindCourseBuilderErrorDismissOnInteract() {
    const form = document.querySelector('.cmrp-filter-form--course-builder');
    if (!form || form.dataset.cmrpCbErrorDismissBound === '1') return;
    form.dataset.cmrpCbErrorDismissBound = '1';

    const dismiss = () => {
      this.hideCourseBuilderErrorMessage();
      form.removeEventListener('change', dismiss);
      form.removeEventListener('input', dismiss);
      delete form.dataset.cmrpCbErrorDismissBound;
    };
    form.addEventListener('change', dismiss);
    form.addEventListener('input', dismiss);
  }

  /**
   * Whether a discovery failure should be retried once before redirecting.
   * @param {string|null} code
   * @returns {boolean}
   */
  isStudentDiscoveryRetryableCode(code) {
    return ['007', '008', '009', '500'].includes(code);
  }

  /**
   * Single Student ID discovery request (ConnectOnce), with client timeout.
   * @param {string} endpoint
   * @param {number} timeoutMs
   */
  async fetchStudentDiscoveryOnce(endpoint, timeoutMs) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      let result = null;
      try {
        result = await response.json();
      } catch (e) {
        result = null;
      }

      return {
        kind: 'response',
        response,
        result,
        code: this.extractStatusCodeFromStudentDiscoveryResponse(result),
        originHttpStatus: Number(result?.origin_http_status),
      };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error?.name === 'AbortError') {
        return { kind: 'timeout' };
      }
      if (typeof window.reportThemeError === 'function') {
        window.reportThemeError(error, { area: 'cm', op: 'student-discovery' });
      }
      return { kind: 'network', error };
    }
  }

  /**
   * Classify a discovery attempt for success / immediate redirect / retryable redirect.
   * @param {{kind: string, response?: Response, result?: object|null, code?: string|null, originHttpStatus?: number, error?: Error}} outcome
   */
  classifyStudentDiscoveryOutcome(outcome) {
    if (outcome.kind === 'timeout') {
      return { type: 'retryable', code: '008' };
    }
    if (outcome.kind === 'network') {
      return { type: 'network', error: outcome.error };
    }

    const { response, result, code, originHttpStatus } = outcome;
    const httpStatus = Number(response?.status);
    const originStatus = Number(originHttpStatus);
    const isGatewayHttpError =
      Number.isFinite(httpStatus) && httpStatus >= 400 && httpStatus < 600;
    const isOriginHttpError =
      Number.isFinite(originStatus) && originStatus >= 400 && originStatus < 600;
    const isHttpError =
      !response?.ok || isGatewayHttpError || isOriginHttpError;

    if (isHttpError) {
      // Gateway (studentIDAPIURL) or ConnectOnce origin 4xx/5xx → retry once, then redirect
      if (isGatewayHttpError || isOriginHttpError) {
        const status = isGatewayHttpError ? httpStatus : originStatus;
        return {
          type: 'retryable',
          code: status >= 500 ? '500' : '008',
        };
      }
      return { type: 'immediate', code: '003' };
    }

    if (!this.isStudentDiscoveryAppSuccess(result)) {
      if (this.isStudentDiscoveryRetryableCode(code)) {
        return { type: 'retryable', code: code || '008' };
      }
      return { type: 'immediate', code: code || '003' };
    }

    const hasSections =
      result?.data &&
      Array.isArray(result.data.sections) &&
      result.data.sections.length > 0;

    if (!hasSections) {
      return { type: 'immediate', code: '004' };
    }

    return { type: 'success', result };
  }

  /**
   * Validate student ID format
   */
  validateStudentId(studentId) {
    const trimmed = String(studentId ?? '').trim();
    if (trimmed.length === 0) {
      return { valid: false, message: `${cmrpStudentIdLabel()} cannot be empty` };
    }
    return { valid: true, message: '' };
  }

  /**
   * Normalize ConnectOnce application codes to three-digit strings.
   * Accepts `error_006`, `Error_010`, `006`, `6`, etc. Returns null for `ok` / unknown.
   * Origin may also send `internal-server-error` on HTTP 200 — map that to `500` (retryable).
   * @param {unknown} raw
   * @returns {string|null}
   */
  normalizeStudentDiscoveryStatusCode(raw) {
    if (raw === undefined || raw === null || raw === '') return null;
    const s = String(raw).trim().toLowerCase();
    if (s === 'ok' || s === 'success') return null;
    // Origin quirk: HTTP 200 body with application-level internal server error
    if (
      s === 'internal-server-error' ||
      s === 'internal_server_error' ||
      s === 'internal server error'
    ) {
      return '500';
    }
    const match = s.match(/^(?:error[_-]?)?(\d+)$/);
    if (!match) return null;
    const n = parseInt(match[1], 10);
    if (Number.isNaN(n)) return null;
    return String(n).padStart(3, '0');
  }

  /**
   * Read ConnectOnce application `code` from the mapped discovery response body.
   * Prefer root `code` (e.g. `error_006`); fall back to legacy numeric fields if present.
   * @param {object|null|undefined} result
   * @returns {string|null} Three-digit code, or null when success / absent
   */
  extractStatusCodeFromStudentDiscoveryResponse(result) {
    if (!result || typeof result !== 'object') return null;
    const raw =
      result.code ??
      result.statusCode ??
      result.status_code ??
      result.errorCode ??
      result.error_code ??
      result.error?.code ??
      result.data?.statusCode ??
      result.data?.status_code;
    return this.normalizeStudentDiscoveryStatusCode(raw);
  }

  /**
   * Whether the mapped discovery body is an application-level success (`code: "ok"`).
   * Do not use a `success` boolean — the gateway no longer emits one.
   * @param {object|null|undefined} result
   * @returns {boolean}
   */
  isStudentDiscoveryAppSuccess(result) {
    if (!result || typeof result !== 'object') return false;
    if (result.success === false) return false;
    const raw = result.code;
    if (raw === undefined || raw === null || raw === '') {
      // Legacy/partial bodies: treat as success only when sections exist.
      return (
        Array.isArray(result.data?.sections) && result.data.sections.length > 0
      );
    }
    const s = String(raw).trim().toLowerCase();
    return s === 'ok' || s === 'success';
  }

  /**
   * Client timeout (ms) for Student ID → ConnectOnce discovery. Theme setting, default 10s.
   * @returns {number}
   */
  getStudentDiscoveryTimeoutMs() {
    const raw = window.shopifySettings?.studentIDAPITimeoutMs;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
    if (Number.isFinite(n) && n > 0) return n;
    return 10000;
  }

  /**
   * Student-ID discovery failure: redirect to course builder with `sidError` and show
   * the mapped message on the course-builder warning banner (no inline CTA link).
   * @param {string|null} code - Three-digit normalized code or null
   */
  showStudentIdDiscoveryErrorByStatusCode(code) {
    this.redirectToCourseBuilderWithError(code);
  }

  async fetchMetaobjects(metaobjectType, query) {
    if (typeof window !== 'undefined' && window.fetchMetaobjectsOnce) {
      return window.fetchMetaobjectsOnce(metaobjectType, query);
    }

    const proxyPath = '/apps/shopifyData/metaobjects';
    const url = `${proxyPath}?type=${metaobjectType}&query=${query}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const activeMetaobjects = result.data.filter(
          item => item.status === 'ACTIVE',
        );
        return activeMetaobjects;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }
  /**
   * Handle student submission from sidebar or mobile drawer.
   * Maps ConnectOnce `code` (e.g. error_006) to a course-builder redirect + banner.
   */
  async handleStudentSubmission(studentData, onComplete, onError, _flowOptions = {}) {
    const { selectedCampus, selectedTerm, studentId } = studentData;
    const showLoader = () => {
      if (typeof this.components.uiStateManager?.showLoadingState === 'function') {
        this.components.uiStateManager.showLoadingState();
      } else if (typeof window.showLoadingState === 'function') {
        window.showLoadingState();
      }
    };
    const hideLoader = () => {
      if (typeof this.components.uiStateManager?.hideLoadingState === 'function') {
        this.components.uiStateManager.hideLoadingState();
      } else if (typeof window.hideLoadingState === 'function') {
        window.hideLoadingState();
      }
    };
    const fail = () => {
      hideLoader();
      // Grid was cleared without empty-state; restore it now that the request failed
      this.components.uiStateManager?.showEmptyState?.();
      onError?.();
    };

    // Hide any existing error messages first
    this.hideErrorMessage();

    // Clear prior course/student results and product grid before a new Student ID search.
    // Keep sticky ATB / format selections. Do not mutate a parked snapshot that
    // belongs to a different flow until this search actually returns course cards.
    const parkedForeign =
      typeof window.CmrpSessionSnapshot?.snapshotMatchesPageFlow === 'function' &&
      !window.CmrpSessionSnapshot.snapshotMatchesPageFlow();
    this.clearAllUserSelections(false, false, {
      skipEmptyState: true,
      preserveAtbSelections: true,
      skipSnapshotMutation: parkedForeign,
    });

    // Enable Student ID mode to hide course selection dropdowns
    if (this.components.sidebar) {
      this.components.sidebar.enableStudentIdMode();
    }

    // Same full-page CMRP loader used elsewhere (#loading-state)
    showLoader();

    // Validate student ID first
    const studentIdValidation = this.validateStudentId(studentId);
    if (!studentIdValidation.valid) {
      this.showErrorMessage(
        `Invalid ${cmrpStudentIdLabel()}: ${studentIdValidation.message}`
      );
      fail();
      return;
    }

    const decodeCmrp = v =>
      typeof window.decodeCmrpHtmlEntities === 'function'
        ? window.decodeCmrpHtmlEntities(v)
        : String(v ?? '');
    const normLabel = value =>
      String(decodeCmrp(value ?? ''))
        .toLowerCase()
        .replace(/\s+/g, '');

    const filteredCampus = this.courseData.ddcs_campus_entity.filter(campus =>
      normLabel(campus.name).includes(normLabel(selectedCampus))
    );
    if (!filteredCampus.length) {
      this.showErrorMessage(
        'Campus not found. Please verify your campus selection.'
      );
      fail();
      return;
    }

    const store_number = filteredCampus[0].store_number;
    const campus_entity_id = filteredCampus[0].campus_entity_id.toUpperCase();
    
    if (!selectedTerm) {
      this.showErrorMessage(
        'Term not found. Please verify your term selection.'
      );
      fail();
      return;
    }

    this.components.sidebar?.resetCmrpCourseBuilderRowAfterStudentSubmit?.();

    const endpoint = `${window.shopifySettings.studentIDAPIURL}/api/discovery/store/${store_number}/campus/${campus_entity_id}/term/${selectedTerm.toUpperCase()}/student/${studentId}`;
    // Test API connectivity first
    const baseApiUrl = window.shopifySettings.studentIDAPIURL;
    const isConnected = await this.testAPIConnectivity(baseApiUrl);

    if (!isConnected) {
      this.showErrorMessage(
        'Unable to connect to the student data service. Please check your internet connection and try again.'
      );
      fail();
      return;
    }

    // Timeout between Shopify and ConnectOnce (theme setting; default 10s)
    const timeoutMs = this.getStudentDiscoveryTimeoutMs();

    const finishWithDiscoveryFailure = (classified, didRetry) => {
      if (classified.type === 'network' && !didRetry) {
        const error = classified.error || new Error('Unknown network error');
        let errorMessage = 'There was a problem fetching student data: ';
        if (String(error.message || '').includes('Failed to fetch')) {
          errorMessage +=
            'Network error or CORS issue. Please check your connection.';
        } else if (String(error.message || '').includes('HTTP')) {
          errorMessage += error.message;
        } else {
          errorMessage += error.message;
        }
        this.showErrorMessage(errorMessage);
        fail();
        return;
      }

      // After a retry, always use the "taking longer" message group
      const redirectCode = didRetry
        ? this.isStudentDiscoveryRetryableCode(classified.code)
          ? classified.code
          : '008'
        : classified.code || '003';
      this.showStudentIdDiscoveryErrorByStatusCode(redirectCode);
      fail();
    };

    const applySuccessfulDiscovery = async result => {
        window.CmrpSessionSnapshot?.adoptIfForeignFlow?.();
        if (this.components.sidebar?.hydrateDdcsDataForCampusTermRow) {
          try {
            await this.components.sidebar.hydrateDdcsDataForCampusTermRow(
              'sid',
              { allowSharedCourseDataOverwrite: true },
            );
          } catch (e) {
            console.error('[CMRP] hydrateDdcsDataForCampusTermRow failed', e);
          }
        }
        const dualCmrp =
          document.getElementById('filters-section')?.dataset
            ?.cmrpStudentIdActive === 'true';
        if (!dualCmrp) {
          document
            .querySelectorAll(
              '.cmrp-filter-form--course-builder .cmrp-course-line-fields',
            )
            .forEach(el => {
              el.style.display = 'none';
            });
        }

        const normalizeCampusName = v =>
          String(v ?? '')
            .toLowerCase()
            .replace(/\s+/g, '');
        const campusEntitySid = window.courseData.ddcs_campus_entity?.find(
          c => normalizeCampusName(c.name) === normalizeCampusName(selectedCampus)
        );
        const campusId = campusEntitySid?.campus_entity_id || '';
        const useDivisionOnFilter = Boolean(
          campusEntitySid?.is_division_used_flag
        );

        const normKey = v =>
          String(v ?? '')
            .toLowerCase()
            .replace(/\s+/g, '');
        const isDefaultPlaceholder = v =>
          String(v ?? '').trim().toLowerCase() === 'dflt';

        // ProductDisplayManager matches division/department by DDCS *name*.
        // ConnectOnce often sends institution_*_id — resolve to DDCS names when possible.
        const findDivision = raw => {
          if (!raw) return null;
          const divisions = window.courseData?.ddcs_campus_division || [];
          return (
            divisions.find(
              d =>
                normKey(d.campus_entity_id) === normKey(campusId) &&
                (normKey(d.name) === normKey(raw) ||
                  normKey(d.campus_division_id) === normKey(raw) ||
                  normKey(d.division_id || '') === normKey(raw))
            ) || null
          );
        };
        const resolveDivisionName = raw => findDivision(raw)?.name || raw || '';

        const resolveDepartmentName = (raw, divisionId = '') => {
          if (!raw) return '';
          const depts = window.courseData?.ddcs_campus_department || [];
          const match = depts.find(d => {
            const sameCampus =
              normKey(d.campus_entity_id) === normKey(campusId);
            const sameDivision =
              !useDivisionOnFilter ||
              !divisionId ||
              normKey(d.campus_division_id) === normKey(divisionId);
            const sameDept =
              normKey(d.name) === normKey(raw) ||
              normKey(d.abbreviation) === normKey(raw) ||
              normKey(d.campus_department_id) === normKey(raw) ||
              normKey(d.department_id || '') === normKey(raw);
            return sameCampus && sameDivision && sameDept;
          });
          return match?.name || raw;
        };

        result.data.sections.forEach(section => {
          const storeRelationship = section.relationships?.find(
            rel => rel.store_id
          );
          const departmentRelationship = section.relationships?.find(
            rel => rel.institution_department_id || rel.institution_department_name
          );
          const courseRelationship = section.relationships?.find(
            rel => rel.institution_course_id || rel.course_name || rel.course_number
          );
          const divisionRelationship = section.relationships?.find(
            rel => rel.institution_division_id
          );
          const departmentRaw =
            departmentRelationship?.institution_department_name ||
            departmentRelationship?.department_name ||
            departmentRelationship?.short_description ||
            departmentRelationship?.institution_department_id ||
            '';
          const courseRaw =
            courseRelationship?.course_number ||
            courseRelationship?.course_code ||
            courseRelationship?.course_name ||
            '';
          const courseValue = courseRaw;

          const divisionRaw =
            divisionRelationship?.short_description ||
            divisionRelationship?.institution_division_id ||
            '';
          const useDivisionForSection =
            useDivisionOnFilter && !isDefaultPlaceholder(divisionRaw);

          const divisionMatch =
            useDivisionForSection && divisionRaw
              ? findDivision(divisionRaw)
              : null;
          const divisionId = divisionMatch?.campus_division_id || '';
          const departmentValue = isDefaultPlaceholder(departmentRaw)
            ? ''
            : resolveDepartmentName(departmentRaw, divisionId);
          // Always set selectedDivision to a string. Leaving it undefined makes
          // ProductDisplayManager treat it as "has division" (`undefined != ''`)
          // and take a campus_entity_id strict-equality path that drops matches.
          const objCourse = {
            selectedDepartment: departmentValue,
            selectedCampus: selectedCampus,
            selectedTerm: selectedTerm,
            selectedTermId: selectedTerm,
            selectedcourse: courseValue,
            selectedsection: section.section_number || section.section_id || section.name || '',
            selectedDivision: useDivisionForSection
              ? divisionMatch?.name || divisionRaw || ''
              : '',
            store_number: storeRelationship.store_id || '',
            fromStudentId: true,
          };

          const wasAdded = this.addCourseToFilters(objCourse);
          if (wasAdded) {
            this.addLabel(objCourse);
          }
        });

        this.components.urlManager.setURLSearchParams();
        try {
          if (typeof window.refreshProductsDisplay === 'function') {
            // Keeps / re-shows loader, then ProductDisplayManager hides when sections render
            await window.refreshProductsDisplay();
          } else {
            hideLoader();
          }
        } catch (e) {
          console.error('[CMRP] refreshProductsDisplay after student ID failed', e);
          hideLoader();
        }
        onComplete?.();
    };

    (async () => {
      let outcome = await this.fetchStudentDiscoveryOnce(endpoint, timeoutMs);
      let classified = this.classifyStudentDiscoveryOutcome(outcome);
      let didRetry = false;

      if (classified.type === 'retryable') {
        didRetry = true;
        outcome = await this.fetchStudentDiscoveryOnce(endpoint, timeoutMs);
        classified = this.classifyStudentDiscoveryOutcome(outcome);
      }

      if (classified.type === 'success') {
        try {
          await applySuccessfulDiscovery(classified.result);
        } catch (e) {
          console.error('[CMRP] applySuccessfulDiscovery failed', e);
          fail();
        }
        return;
      }

      finishWithDiscoveryFailure(classified, didRetry);
    })();
  }

  /**
   * Add course to filters, preventing duplicates
   * @param {Object} course - Course object to add
   * @returns {boolean} - True if course was added, false if duplicate
   */
  addCourseToFilters(course) {
    // Validate course object
    if (!course || typeof course !== 'object') {
      return false;
    }
    
    // Check if course already exists
    const isDuplicate = this.selectedFilters.some(
      filter =>
        filter.selectedCampus === (course.selectedCampus || course.selectedcampus) &&
        filter.selectedTerm === (course.selectedTerm || course.selectedterm) &&
        (filter.selectedDivision || '') ===
          (course.selectedDivision || course.selecteddivision || '') &&
        filter.selectedDepartment === (course.selectedDepartment || course.selecteddepartment) &&
        filter.selectedcourse === (course.selectedcourse || course.course) &&
        filter.selectedsection === (course.selectedsection || course.section)
    );

    if (isDuplicate) {
      return false;
    }

    this.selectedFilters.push(course);
    return true;
  }

  /**
   * Clear only courses that came from Student ID search
   */
  clearStudentIdCourses() {
    // Remove courses from selectedFilters that came from Student ID
    const coursesToRemove = this.selectedFilters.filter(
      filter => filter.fromStudentId
    );
    this.selectedFilters = this.selectedFilters.filter(
      filter => !filter.fromStudentId
    );

    // Remove corresponding course labels from UI
    const courseList = document.getElementById('course-list');
    if (courseList && coursesToRemove.length > 0) {
      coursesToRemove.forEach(course => {
        const divisionAttr = course.selectedDivision
          ? `[data-division="${CSS.escape(String(course.selectedDivision))}"]`
          : '';
        const courseItem = courseList.querySelector(
          `.course-item${divisionAttr}[data-department="${course.selectedDepartment}"][data-course="${course.selectedcourse}"][data-section="${course.selectedsection}"]`
        );
        if (courseItem) {
          courseItem.remove();
        }
      });

      // Hide form actions container if no courses remain
      const formActionsContainer = document.getElementById(
        'form-actions-container'
      );
      if (formActionsContainer && courseList.children.length === 0) {
        formActionsContainer.style.display = 'none';
      }
    }
  }

  /**
   * Clear all user selections
   */
  clearAllUserSelections(clearCampus = false, clearTerm = false, options = {}) {
    // URL restore rebuilds filters + products; a concurrent sidebar clear must not
    // wipe the restored DOM mid-flight (products end up under a hidden form / empty).
    if (
      this.components.urlManager?.isUrlRestorePending?.() &&
      !options.forceDuringUrlRestore
    ) {
      return;
    }

    const skipBrokenResultsWipe =
      this.components.urlManager?.isBrokenCmrpUrlResolution?.() === true;

    this.selectedFilters.length = 0;

    const courseList = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (courseList) courseList.innerHTML = '';
    if (formActionsContainer) formActionsContainer.style.display = 'none';

    const filteredProductsEl = document.getElementById('filtered-products');
    if (filteredProductsEl && !skipBrokenResultsWipe) {
      filteredProductsEl.innerHTML = '';
    }

    // Courses left the page; keep the ATB queue. Snapshot is only wiped on
    // timer expiry, successful add-to-bag, or adoptIfForeignFlow.
    if (
      !options.skipSnapshotMutation &&
      !this.components.urlManager?.isUrlRestorePending?.() &&
      !skipBrokenResultsWipe
    ) {
      window.CmrpSessionSnapshot?.clearLastResultsUrl?.();
    }

    if (!options.skipEmptyState && !skipBrokenResultsWipe) {
      this.components.uiStateManager.showEmptyState();
    }

    if (clearCampus) {
      const campusSelect =
        document.getElementById('campus-cb') ||
        (typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('campus')
          : document.querySelector('#campus-term-dropdowns #campus'));
      if (campusSelect) {
        campusSelect.value = '';
        campusSelect.disabled = false;
      }
    }

    if (
      this.components.urlManager &&
      !this.components.urlManager.isUrlRestorePending?.() &&
      !skipBrokenResultsWipe
    ) {
      this.components.urlManager.setURLSearchParams();
    }

    if (!skipBrokenResultsWipe) {
      if (this.components.navigationManager) {
        this.components.navigationManager.hideNavigationPanels();
      } else {
        const navPanel = document.getElementById('right-panel-nav');
        const campusTermBar = document.getElementById('campus-term-info');
        const chipsContainer = document.getElementById('department-filter-chips');
        if (navPanel) navPanel.style.display = 'none';
        if (campusTermBar) campusTermBar.style.display = 'none';
        if (chipsContainer) chipsContainer.innerHTML = '';
      }
    }
    const courses_container = document.getElementById(
      typeof window.cmrpId === 'function'
        ? window.cmrpId('selected-courses-container', 'cb')
        : 'selected-courses-container',
    );
    if (courses_container) courses_container.innerHTML = '';

    this.removeAllMobileTags();
    if (typeof window.courseMaterialsSidebar?.syncBubblesFromFilters === 'function') {
      window.courseMaterialsSidebar.syncBubblesFromFilters([]);
    }
  }

  clearAllTermSelections(clearTerm = false) {
    // URL deep-link restore: do not wipe hydrated filters / grid mid-cascade.
    if (this.components.urlManager?.isUrlRestorePending?.()) {
      return;
    }
    // Broken-params autoselect must not destroy the term-not-found card.
    if (this.components.urlManager?.isBrokenCmrpUrlResolution?.()) {
      return;
    }

    this.selectedFilters.length = 0;

    const courseList = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (courseList) courseList.innerHTML = '';
    if (formActionsContainer) formActionsContainer.style.display = 'none';

    const filteredProductsEl = document.getElementById('filtered-products');
    if (filteredProductsEl) filteredProductsEl.innerHTML = '';

    this.components.uiStateManager.showEmptyState();

    
    if (
      this.components.urlManager &&
      !this.components.urlManager.isUrlRestorePending?.()
    ) {
      this.components.urlManager.setURLSearchParams();
    }

    if (this.components.navigationManager) {
      this.components.navigationManager.hideNavigationPanels();
    } else {
      const navPanel = document.getElementById('right-panel-nav');
      const campusTermBar = document.getElementById('campus-term-info');
      const chipsContainer = document.getElementById('department-filter-chips');
      if (navPanel) navPanel.style.display = 'none';
      if (campusTermBar) campusTermBar.style.display = 'none';
      if (chipsContainer) chipsContainer.innerHTML = '';
    }
    const courses_container = document.getElementById(
      typeof window.cmrpId === 'function'
        ? window.cmrpId('selected-courses-container', 'cb')
        : 'selected-courses-container',
    );
    if (courses_container) courses_container.innerHTML = '';

    this.removeAllMobileTags();
    if (typeof window.courseMaterialsSidebar?.syncBubblesFromFilters === 'function') {
      window.courseMaterialsSidebar.syncBubblesFromFilters([]);
    }
  }

  /**
   * Refresh products display (internal method)
   */
  refreshProductsDisplay() {
    // Call the global method to ensure consistency
    if (window.refreshProductsDisplay) {
      window.refreshProductsDisplay();
    } else {
      this.components.uiStateManager.showLoadingState();
    }
  }

  /**
   * Show campus change warning (for backward compatibility)
   */
  showCampusChangeWarning() {
    if (typeof showWarningMessage === 'function') {
      const wid =
        typeof window.cmrpId === 'function'
          ? window.cmrpId('campus-change-warning', 'activeCt')
          : 'campus-change-warning';
      showWarningMessage(wid);
    }
  }

  /**
   * Hide campus change warning (for backward compatibility)
   */
  hideCampusChangeWarning() {
    if (typeof hideWarningMessage === 'function') {
      const wid =
        typeof window.cmrpId === 'function'
          ? window.cmrpId('campus-change-warning', 'activeCt')
          : 'campus-change-warning';
      hideWarningMessage(wid);
    }
  }
}

// Make the class available globally
window.CourseMaterialsPage = CourseMaterialsPage;
